import { useLocalSearchParams, useRouter } from 'expo-router';
import { useState } from 'react';
import { View } from 'react-native';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { useCancelReservation } from '@/api/partners/reservations/use-cancel-reservation';
import { useReservation } from '@/api/partners/reservations/use-reservation';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { formatNumber } from '@/core';
import { Button, showErrorMessage, Text } from '@/ui';
import { InputMulti } from '@/ui/input-multi';
import { Loader } from '@/ui/loader';

export default function CancelRental() {
  const { styles, theme } = useStyles(stylesheet);
  const local = useLocalSearchParams();
  const router = useRouter();
  const [reasonText, setReasonText] = useState('');

  const {
    data: reservationData,
    refetch: refetchReservation,
    isLoading: isLoadingReservation,
  } = useReservation({
    //@ts-ignore
    variables: { id: local.id },
    enabled: !!local.id,
  });

  const { mutateAsync: cancelReservation, isPending: isCancellingReservation } =
    useCancelReservation();

  const handleCancellationRequest = async () => {
    try {
      await cancelReservation({
        id: reservationData!.id,
        ownerCancellationReason: reasonText,
      });

      router.navigate('/partner/reservations');

      Toast.show({
        text1: 'Trip cancelled successfully!',
        type: 'success',
      });
    } catch (e: any) {
      showErrorMessage(e.message);
    }
  };

  if (isLoadingReservation || isCancellingReservation) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ height: 250, width: 250 }} />
      </View>
    );
  }

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: theme.colors.background_1,
        paddingTop: 16,
      }}
    >
      <View style={styles.container}>
        <View style={styles.nav}>
          <NavigationButton
            type={NavigationButtonTypes.back_with_text}
            onPress={() => {
              router.navigate(`/partner/reservations/${reservationData.id}`);
            }}
          />

          <Text type="title1">Cancel Rental</Text>
        </View>

        <View style={{ gap: 32 }}>
          <Text
            type="bodyRegular"
            style={{ color: theme.colors.text.secondary }}
          >
            To avoid cancellation penalties and ensure a smooth experience for
            your guest, try to move them into another car first.
          </Text>

          <Text
            type="bodyRegular"
            style={{ color: theme.colors.text.secondary }}
          >
            You will miss out on ${formatNumber(reservationData.ownerEarnings)}{' '}
            in earnings and you may be subject to a $50 cancellation penalty
            depending on the cancellation reason.
          </Text>
        </View>

        <View style={styles.inputContainer}>
          <InputMulti
            label="Reason for cancelling"
            text={reasonText || ''}
            onChangeText={(s: string) => setReasonText(s)}
            placeholder="Please provide a reason for cancelling"
            style={{ minHeight: 100 }}
          />

          <View
            style={{
              alignItems: 'flex-end',
            }}
          >
            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              Minimum of 10 characters required
            </Text>
          </View>
        </View>

        <View style={styles.buttonDesktopContainer}>
          <Button
            text="Submit Cancellation Request"
            type="primary"
            onPress={handleCancellationRequest}
            disabled={reasonText.length < 10}
          />
        </View>
      </View>

      <View style={styles.buttonMobileContainer}>
        <Button
          text="Submit Cancellation Request"
          type="primary"
          onPress={handleCancellationRequest}
          disabled={reasonText.length < 10}
        />
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flex: 1,
    padding: 16,
    paddingLeft: {
      md: 56,
    },
    gap: 32,
  },
  nav: {
    display: {
      xs: 'none',
      md: 'flex',
    },
    gap: 16,
  },
  inputContainer: {
    gap: 8,
    width: {
      md: 700,
    },
  },
  buttonMobileContainer: {
    padding: 16,
    display: {
      xs: 'flex',
      md: 'none',
    },
  },
  buttonDesktopContainer: {
    display: {
      xs: 'none',
      md: 'flex',
    },
    width: 300,
  },
}));
